// frontend/src/components/EmpresaPortal/ConstructorPrueba.js
//
// Página completa para crear/editar una prueba personalizada ("crea tu
// propia prueba"), inspirada en el constructor de Microsoft Forms: lienzo
// centrado, portada con título/instrucciones, fila de "inicio rápido" para
// agregar preguntas por tipo, y tarjetas de pregunta editables en línea.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './ConstructorPrueba.css';

const TIPOS_PREGUNTA = [
  { value: 'opcion_unica', label: 'Opción única', icono: '🔘', descripcion: 'Una sola respuesta correcta o a elegir' },
  { value: 'opcion_multiple', label: 'Opción múltiple', icono: '☑️', descripcion: 'Puede marcar varias opciones' },
  { value: 'texto_abierto', label: 'Texto abierto', icono: '📝', descripcion: 'Respuesta libre, sin calificar' },
  { value: 'escala', label: 'Escala', icono: '📊', descripcion: 'Calificación numérica, ej. del 1 al 5' }
];

const letraOpcion = (index) => String.fromCharCode(97 + index);

// Identificador local (nunca viaja al backend) para usar como key de React
// en vez del índice: así, al reordenar con drag-and-drop, cada tarjeta
// conserva su propio estado/foco en vez de que React reutilice el nodo DOM
// de la posición que cambió de lugar.
let contadorKeys = 0;
const generarKey = () => `q-${contadorKeys++}`;

const nuevaPregunta = (tipo) => {
  const base = { _key: generarKey() };
  if (tipo === 'opcion_unica' || tipo === 'opcion_multiple') {
    return { ...base, texto: '', tipo_pregunta: tipo, calificable: true, opciones: { a: '', b: '' }, respuesta_correcta: tipo === 'opcion_multiple' ? [] : '' };
  }
  if (tipo === 'escala') {
    return { ...base, texto: '', tipo_pregunta: 'escala', calificable: false, opciones: { min: 1, max: 5, etiqueta_min: '', etiqueta_max: '' }, respuesta_correcta: null };
  }
  return { ...base, texto: '', tipo_pregunta: 'texto_abierto', calificable: false, opciones: null, respuesta_correcta: null };
};

const ConstructorPrueba = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = !!id;

  const [nombre, setNombre] = useState('');
  const [instrucciones, setInstrucciones] = useState('');
  const [tiempoLimite, setTiempoLimite] = useState('');
  const [reactivos, setReactivos] = useState([]);
  const [cargando, setCargando] = useState(editando);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const refsPreguntas = useRef([]);
  const dragIndexRef = useRef(null);
  const [arrastrandoIndex, setArrastrandoIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  useEffect(() => {
    if (editando) cargarPrueba();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cargarPrueba = async () => {
    setCargando(true);
    try {
      const res = await fetch(`/api/empresas/me/pruebas/${id}`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setNombre(data.nombre);
      setInstrucciones(data.instrucciones || '');
      setTiempoLimite(data.tiempo_limite_segundos || '');
      setReactivos(data.reactivos.map(r => ({
        _key: generarKey(),
        texto: r.texto,
        tipo_pregunta: r.tipo_pregunta,
        calificable: r.calificable,
        opciones: r.opciones || (r.tipo_pregunta === 'escala' ? { min: 1, max: 5, etiqueta_min: '', etiqueta_max: '' } : { a: '', b: '' }),
        respuesta_correcta: r.tipo_pregunta === 'opcion_multiple'
          ? (r.respuesta_correcta ? JSON.parse(r.respuesta_correcta) : [])
          : (r.respuesta_correcta || '')
      })));
    } catch (e) {
      setError('No se pudo cargar la prueba: ' + e.message);
    } finally {
      setCargando(false);
    }
  };

  const agregarPreguntaTipo = (tipo) => {
    setReactivos(prev => [...prev, nuevaPregunta(tipo)]);
    setTimeout(() => {
      const ultimo = refsPreguntas.current[refsPreguntas.current.length - 1];
      ultimo?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const eliminarPregunta = (index) => setReactivos(prev => prev.filter((_, i) => i !== index));

  const actualizarPregunta = (index, cambios) => {
    setReactivos(prev => prev.map((r, i) => i === index ? { ...r, ...cambios } : r));
  };

  const cambiarTipoPregunta = (index, tipo) => {
    setReactivos(prev => prev.map((r, i) => i === index ? { ...nuevaPregunta(tipo), _key: r._key, texto: r.texto } : r));
  };

  // ============================================
  // Reordenar preguntas: arrastrar con el mouse (drag-and-drop nativo) o,
  // como alternativa accesible/táctil, los botones ▲▼.
  // ============================================
  const moverPregunta = (desde, hasta) => {
    if (hasta < 0 || hasta >= reactivos.length || desde === hasta) return;
    setReactivos(prev => {
      const copia = [...prev];
      const [movido] = copia.splice(desde, 1);
      copia.splice(hasta, 0, movido);
      return copia;
    });
  };

  const handleDragStart = (index) => (e) => {
    dragIndexRef.current = index;
    setArrastrandoIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const cardEl = refsPreguntas.current[index];
    if (cardEl) {
      try { e.dataTransfer.setDragImage(cardEl, 24, 24); } catch (err) { /* algunos navegadores lo ignoran, no pasa nada */ }
    }
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (index) => (e) => {
    e.preventDefault();
    const desde = dragIndexRef.current;
    if (desde !== null && desde !== index) {
      moverPregunta(desde, index);
    }
    dragIndexRef.current = null;
    setArrastrandoIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setArrastrandoIndex(null);
    setDragOverIndex(null);
  };

  const agregarOpcion = (index) => {
    setReactivos(prev => prev.map((r, i) => {
      if (i !== index) return r;
      const siguienteLetra = letraOpcion(Object.keys(r.opciones).length);
      return { ...r, opciones: { ...r.opciones, [siguienteLetra]: '' } };
    }));
  };

  const eliminarOpcion = (index, key) => {
    setReactivos(prev => prev.map((r, i) => {
      if (i !== index) return r;
      const nuevasOpciones = { ...r.opciones };
      delete nuevasOpciones[key];
      let nuevaRespuesta = r.respuesta_correcta;
      if (r.tipo_pregunta === 'opcion_unica' && nuevaRespuesta === key) nuevaRespuesta = '';
      if (r.tipo_pregunta === 'opcion_multiple') nuevaRespuesta = (nuevaRespuesta || []).filter(k => k !== key);
      return { ...r, opciones: nuevasOpciones, respuesta_correcta: nuevaRespuesta };
    }));
  };

  const actualizarOpcionTexto = (index, key, texto) => {
    setReactivos(prev => prev.map((r, i) => i === index ? { ...r, opciones: { ...r.opciones, [key]: texto } } : r));
  };

  const toggleRespuestaCorrecta = (index, key) => {
    setReactivos(prev => prev.map((r, i) => {
      if (i !== index) return r;
      if (r.tipo_pregunta === 'opcion_unica') return { ...r, respuesta_correcta: key };
      const actual = r.respuesta_correcta || [];
      const nueva = actual.includes(key) ? actual.filter(k => k !== key) : [...actual, key];
      return { ...r, respuesta_correcta: nueva };
    }));
  };

  const handleCancelar = () => navigate('/panel');

  const handleGuardar = async () => {
    setError('');
    if (!nombre.trim()) {
      setError('Ponle un nombre a tu prueba antes de guardar.');
      return;
    }
    if (reactivos.length === 0) {
      setError('Agrega al menos una pregunta usando las opciones de "Inicio rápido".');
      return;
    }
    setGuardando(true);
    try {
      const url = editando ? `/api/empresas/me/pruebas/${id}` : '/api/empresas/me/pruebas';
      const method = editando ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nombre,
          instrucciones,
          tiempo_limite_segundos: tiempoLimite ? parseInt(tiempoLimite, 10) : null,
          reactivos: reactivos.map(({ _key, ...resto }) => resto)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'No se pudo guardar la prueba');
      navigate('/panel');
    } catch (err) {
      setError(err.message);
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="cp-loading">Cargando prueba...</div>;
  }

  const renderBotonesTipo = (compacto) => (
    <div className={`cp-tipos-grid ${compacto ? 'cp-tipos-grid-compacto' : ''}`}>
      {TIPOS_PREGUNTA.map(t => (
        <button type="button" key={t.value} className="cp-tipo-btn" onClick={() => agregarPreguntaTipo(t.value)}>
          <span className="cp-tipo-icono">{t.icono}</span>
          <span className="cp-tipo-texto">
            <strong>{t.label}</strong>
            {!compacto && <small>{t.descripcion}</small>}
          </span>
        </button>
      ))}
    </div>
  );

  return (
    <div className="cp-page">
      <header className="cp-topbar">
        <div className="cp-brand">
          <img src="/isotipotransparente.png" alt="PsyTest" className="cp-brand-logo" />
          <div className="cp-brand-text">
            <span className="cp-brand-title">PsyTest</span>
            <span className="cp-sub">
              <button type="button" className="cp-breadcrumb-link" onClick={handleCancelar}>Mis pruebas</button>
              <span className="cp-breadcrumb-sep">›</span>
              {nombre || 'Prueba sin título'}
            </span>
          </div>
        </div>
        <div className="cp-topbar-right">
          <button type="button" className="cp-btn-secondary" onClick={handleCancelar} disabled={guardando}>Cancelar</button>
          <button type="button" className="cp-btn-primary" onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : (editando ? 'Guardar cambios' : 'Crear prueba')}
          </button>
        </div>
      </header>

      <div className="cp-canvas-wrap">
        <div className="cp-canvas">
          <div className="cp-portada">
            <input
              className="cp-portada-titulo"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Prueba sin título"
            />
            <textarea
              className="cp-portada-instrucciones"
              value={instrucciones}
              onChange={(e) => setInstrucciones(e.target.value)}
              placeholder="Agrega una descripción o instrucciones para el candidato (opcional)"
              rows={2}
            />
            <div className="cp-portada-config">
              <label>⏱️ Tiempo límite en segundos (opcional)</label>
              <input
                type="number"
                min="1"
                value={tiempoLimite}
                onChange={(e) => setTiempoLimite(e.target.value)}
                placeholder="Sin límite"
              />
            </div>
          </div>

          <div className="cp-inicio-rapido">
            <span className="cp-inicio-rapido-label">Inicio rápido con</span>
            {renderBotonesTipo(false)}
          </div>

          {reactivos.map((r, index) => (
            <div
              key={r._key}
              className={[
                'cp-pregunta-card',
                arrastrandoIndex === index ? 'cp-pregunta-card-arrastrando' : '',
                dragOverIndex === index && arrastrandoIndex !== null && arrastrandoIndex !== index ? 'cp-pregunta-card-dragover' : ''
              ].filter(Boolean).join(' ')}
              ref={(el) => { refsPreguntas.current[index] = el; }}
              onDragOver={handleDragOver(index)}
              onDrop={handleDrop(index)}
            >
              <div className="cp-pregunta-header">
                <span
                  className="cp-drag-handle"
                  draggable
                  onDragStart={handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  title="Arrastrar para reordenar"
                >
                  ⠿
                </span>
                <div className="cp-reordenar-botones">
                  <button type="button" onClick={() => moverPregunta(index, index - 1)} disabled={index === 0} title="Mover arriba">▲</button>
                  <button type="button" onClick={() => moverPregunta(index, index + 1)} disabled={index === reactivos.length - 1} title="Mover abajo">▼</button>
                </div>
                <span className="cp-pregunta-numero">{index + 1}</span>
                <span className="cp-pregunta-tipo-badge">
                  {TIPOS_PREGUNTA.find(t => t.value === r.tipo_pregunta)?.icono} {TIPOS_PREGUNTA.find(t => t.value === r.tipo_pregunta)?.label}
                </span>
                <div className="cp-pregunta-header-acciones">
                  <select value={r.tipo_pregunta} onChange={(e) => cambiarTipoPregunta(index, e.target.value)} className="cp-select-tipo" title="Cambiar tipo de pregunta">
                    {TIPOS_PREGUNTA.map(t => <option key={t.value} value={t.value}>{t.icono} {t.label}</option>)}
                  </select>
                  <button type="button" className="cp-btn-quitar-pregunta" onClick={() => eliminarPregunta(index)} title="Eliminar pregunta">🗑️</button>
                </div>
              </div>

              <textarea
                className="cp-pregunta-texto"
                value={r.texto}
                onChange={(e) => actualizarPregunta(index, { texto: e.target.value })}
                rows={1}
                placeholder="Escribe tu pregunta..."
              />

              {(r.tipo_pregunta === 'opcion_unica' || r.tipo_pregunta === 'opcion_multiple') && (
                <>
                  <div className="cp-opciones-lista">
                    {Object.entries(r.opciones).map(([key, texto]) => {
                      const esCorrecta = r.tipo_pregunta === 'opcion_unica' ? r.respuesta_correcta === key : (r.respuesta_correcta || []).includes(key);
                      return (
                        <div key={key} className={`cp-opcion-row ${esCorrecta ? 'cp-opcion-row-correcta' : ''}`}>
                          <span className="cp-opcion-letra">{key.toUpperCase()}</span>
                          <input
                            type="text"
                            value={texto}
                            onChange={(e) => actualizarOpcionTexto(index, key, e.target.value)}
                            placeholder={`Opción ${key.toUpperCase()}`}
                          />
                          <label className="cp-opcion-marcar" title="Marcar como respuesta correcta">
                            <input
                              type={r.tipo_pregunta === 'opcion_unica' ? 'radio' : 'checkbox'}
                              name={`correcta-${index}`}
                              checked={esCorrecta}
                              onChange={() => toggleRespuestaCorrecta(index, key)}
                              disabled={!r.calificable}
                            />
                            <span>Correcta</span>
                          </label>
                          {Object.keys(r.opciones).length > 2 && (
                            <button type="button" className="cp-btn-quitar-opcion" onClick={() => eliminarOpcion(index, key)} title="Quitar opción">✕</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <button type="button" className="cp-btn-agregar-opcion" onClick={() => agregarOpcion(index)}>+ Agregar opción</button>

                  <label className="cp-toggle-calificable">
                    <input
                      type="checkbox"
                      checked={r.calificable}
                      onChange={(e) => actualizarPregunta(index, { calificable: e.target.checked, respuesta_correcta: r.tipo_pregunta === 'opcion_multiple' ? [] : '' })}
                    />
                    <span>
                      <strong>Esta pregunta se califica</strong>
                      <small>{r.calificable ? 'Marca "Correcta" junto a la opción correcta' : 'Se guardará como referencia, sin calificar'}</small>
                    </span>
                  </label>
                </>
              )}

              {r.tipo_pregunta === 'escala' && (
                <div className="cp-field-row">
                  <div className="cp-field">
                    <label>Mínimo</label>
                    <input type="number" value={r.opciones.min} onChange={(e) => actualizarPregunta(index, { opciones: { ...r.opciones, min: parseInt(e.target.value, 10) } })} />
                  </div>
                  <div className="cp-field">
                    <label>Máximo</label>
                    <input type="number" value={r.opciones.max} onChange={(e) => actualizarPregunta(index, { opciones: { ...r.opciones, max: parseInt(e.target.value, 10) } })} />
                  </div>
                  <div className="cp-field">
                    <label>Etiqueta mínimo (opcional)</label>
                    <input type="text" value={r.opciones.etiqueta_min} onChange={(e) => actualizarPregunta(index, { opciones: { ...r.opciones, etiqueta_min: e.target.value } })} />
                  </div>
                  <div className="cp-field">
                    <label>Etiqueta máximo (opcional)</label>
                    <input type="text" value={r.opciones.etiqueta_max} onChange={(e) => actualizarPregunta(index, { opciones: { ...r.opciones, etiqueta_max: e.target.value } })} />
                  </div>
                </div>
              )}

              {r.tipo_pregunta === 'texto_abierto' && (
                <p className="cp-hint">El candidato podrá escribir una respuesta libre. No se califica automáticamente.</p>
              )}
            </div>
          ))}

          {reactivos.length > 0 && (
            <div className="cp-agregar-mas">
              <span className="cp-inicio-rapido-label">Agregar otra pregunta</span>
              {renderBotonesTipo(true)}
            </div>
          )}

          {error && <div className="cp-error">{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default ConstructorPrueba;
